import { useState, useRef } from 'preact/hooks'
import { apiClient, InitData } from '../../core'
import { t } from '../../core/i18n'

interface Props {
  initData: InitData
  title?: string | null
  subtitle?: string | null
  buttonText?: string | null
}

/**
 * NewsletterWidget — mirrors HS-native styling.
 *
 * Centered card layout:
 *   - Title ("Tilmeld nyhedsbrev")
 *   - Subtitle/description (gray, 0.95rem)
 *   - Email input (1px border, padding 0.85rem 1rem) + filled orange "Tilmeld" button
 *   - Success state: green ✓ with thank-you message
 *   - Error state: red border on input + red error text below
 *   - Loading state: button disabled, label "Tilmelder..."
 */
export function NewsletterWidget({ initData, title = null, subtitle = null, buttonText = null }: Props) {
  const str = t(initData.locale)
  // Attribute overrides from the embedding page win; defaults follow the
  // tenant locale so a non-Danish site never shows Danish copy.
  const heading = title || str.newsletterTitle
  const sub = subtitle || str.newsletterSubtitle
  const submitLabel = buttonText || str.newsletterSubmit
  const [email, setEmail] = useState('')
  const [website, setWebsite] = useState('') // honeypot
  const [submitting, setSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Hvornår formularen blev vist. /api/widget/newsletter læser composeMs i
  // spamtjekket (lib/spam-check.ts) som kontakt-widgetten. Widgetten sendte før
  // formStartedAt, som ingen rute læste, så «for hurtig»-tjekket fandtes ikke.
  // Målt fra visningen, IKKE fra e-mailfeltets fokus: feltet autofyldes, så en
  // ægte tilmelding kan tage under et sekund fra fokus til klik.
  const shownAtRef = useRef<number>(Date.now())

  async function handleSubmit(e: Event) {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    setError(null)

    const result = await apiClient.post('/api/widget/newsletter', {
      email,
      website,
      composeMs: Date.now() - shownAtRef.current,
    })

    setSubmitting(false)

    if (result.error) {
      setError(result.error)
    } else {
      setSubmitted(true)
    }
  }

  if (submitted) {
    return (
      <div>
        <style>{newsletterStyles}</style>
        <div class="hsw-news-card hsw-news-success">
          <div class="hsw-news-check-circle">
            <span class="hsw-news-check-icon">✓</span>
          </div>
          <h3 class="hsw-news-title">{str.newsletterThanksTitle}</h3>
          <p class="hsw-news-desc">{str.newsletterThanksBody}</p>
        </div>
      </div>
    )
  }

  return (
    <div>
      <style>{newsletterStyles}</style>
      <div class="hsw-news-card">
        <h3 class="hsw-news-title">{heading}</h3>
        <p class="hsw-news-desc">{sub}</p>

        <form onSubmit={handleSubmit} class="hsw-news-form">
          {/* Honeypot — visually hidden; bots fill it, humans skip it */}
          <div class="hsw-news-honeypot" aria-hidden="true">
            <label>
              Website
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autocomplete="off"
                value={website}
                onInput={(e) => setWebsite((e.target as HTMLInputElement).value)}
              />
            </label>
          </div>
          <input
            type="email"
            class={`hsw-news-input${error ? ' hsw-news-input-error' : ''}`}
            placeholder={str.emailPlaceholder}
            aria-label={str.fieldEmail}
            value={email}
            onInput={(e) => {
              setEmail((e.target as HTMLInputElement).value)
              if (error) setError(null)
            }}
            required
            disabled={submitting}
            aria-invalid={error ? 'true' : 'false'}
            aria-describedby={error ? 'hsw-news-error' : undefined}
          />
          <button
            type="submit"
            class="hsw-news-btn"
            disabled={submitting}
          >
            {submitting ? str.newsletterSubmitting : submitLabel}
          </button>
        </form>

        {error && (
          <div id="hsw-news-error" class="hsw-news-error" role="alert">
            {error}
          </div>
        )}
      </div>
    </div>
  )
}

const newsletterStyles = `
  .hsw-news-card {
    background: var(--yoga-card-bg, #ffffff);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    padding: 2.5rem 2rem;
    max-width: 520px;
    margin: 0 auto;
    text-align: center;
  }
  .hsw-news-title {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0 0 0.5rem;
    color: var(--yoga-text, #0a0a0a);
    letter-spacing: -0.01em;
  }
  .hsw-news-desc {
    font-size: 0.95rem;
    color: var(--yoga-text-muted, #525252);
    margin: 0 0 1.5rem;
    line-height: 1.5;
  }
  .hsw-news-form {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    align-items: stretch;
  }
  .hsw-news-honeypot {
    position: absolute;
    left: -9999px;
    top: auto;
    width: 1px;
    height: 1px;
    overflow: hidden;
  }
  .hsw-news-input {
    flex: 1 1 220px;
    min-width: 0;
    padding: 0.85rem 1rem;
    border: 1px solid var(--yoga-card-border, #d4d4d4);
    border-radius: var(--yoga-radius, 0);
    background: var(--yoga-card-bg, #ffffff);
    color: var(--yoga-text, #0a0a0a);
    font-size: 0.95rem;
    font-family: inherit;
    line-height: 1.4;
    transition: border-color .2s, box-shadow .2s;
    box-sizing: border-box;
  }
  .hsw-news-input:focus {
    outline: none;
    border-color: var(--yoga-primary, #FF6B35);
    box-shadow: 0 0 0 3px rgba(255,107,53,0.15);
  }
  .hsw-news-input:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .hsw-news-input::placeholder {
    color: var(--yoga-text-muted, #a3a3a3);
  }
  .hsw-news-input-error {
    border-color: #dc2626;
  }
  .hsw-news-input-error:focus {
    border-color: #dc2626;
    box-shadow: 0 0 0 3px rgba(220,38,38,0.15);
  }
  .hsw-news-btn {
    flex: 0 0 auto;
    padding: 0.85rem 1.5rem;
    background: var(--yoga-primary, #FF6B35);
    color: #ffffff;
    border: 1px solid var(--yoga-primary, #FF6B35);
    border-radius: var(--yoga-radius, 0);
    font-size: 0.95rem;
    font-weight: 600;
    font-family: inherit;
    letter-spacing: 0.01em;
    cursor: pointer;
    transition: background-color .2s, border-color .2s, opacity .2s;
    white-space: nowrap;
  }
  .hsw-news-btn:hover:not(:disabled) {
    background: var(--yoga-primary-900, #1A1A2E);
    border-color: var(--yoga-primary-900, #1A1A2E);
  }
  .hsw-news-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
  .hsw-news-error {
    margin-top: 0.75rem;
    text-align: left;
    color: #dc2626;
    font-size: 0.875rem;
    line-height: 1.4;
  }
  .hsw-news-success {
    padding-top: 2rem;
    padding-bottom: 2rem;
  }
  .hsw-news-check-circle {
    width: 56px;
    height: 56px;
    border-radius: 999px;
    background: rgba(16,185,129,0.12);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 1rem;
  }
  .hsw-news-check-icon {
    color: #10b981;
    font-size: 1.75rem;
    font-weight: 700;
    line-height: 1;
  }
  @media (max-width: 480px) {
    .hsw-news-card {
      padding: 2rem 1.25rem;
    }
    .hsw-news-form {
      flex-direction: column;
    }
    .hsw-news-input,
    .hsw-news-btn {
      width: 100%;
    }
  }
`
